import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import './PostJob.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.alooma.mg';

// Digits only -> comma-grouped string for money inputs
const formatMoney = (val) => {
  const digits = String(val ?? '').replace(/[^\d]/g, '');
  if (!digits) return '';
  return parseInt(digits, 10).toLocaleString('en-US');
};

function PostJob() {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const isEdit = !!jobId;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    contentType: 'feed',
    postCount: 1,
    storyCount: 0,
    budget: '',
    creatorCount: 1,
    deadline: '',
    requirements: '',
    minFollowers: '',
    niche: '',
  });

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');

    if (!storedUser || !token) {
      navigate('/network');
      return;
    }

    const userData = JSON.parse(storedUser);
    if (userData.userType !== 'brand') {
      navigate('/network/influencer-dashboard');
      return;
    }

    if (isEdit) {
      (async () => {
        try {
          const response = await fetch(`${API_URL}/api/jobs/${jobId}`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          const data = await response.json();
          if (response.ok && data.job) {
            const j = data.job;
            setFormData({
              title: j.title || '',
              description: j.description || '',
              contentType: j.contentType || 'feed',
              postCount: j.postCount || 1,
              storyCount: j.storyCount || 0,
              budget: j.budget ? String(j.budget) : '',
              creatorCount: j.creatorCount || 1,
              deadline: j.deadline ? new Date(j.deadline).toISOString().split('T')[0] : '',
              requirements: j.requirements || '',
              minFollowers: j.minFollowers ? String(j.minFollowers) : '',
              niche: j.niche || '',
            });
          }
        } catch (err) {
          setError('Failed to load job for editing');
        }
      })();
    }
  }, [navigate, isEdit, jobId]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'number' ? (value === '' ? '' : parseInt(value)) : value,
    });
    setError('');
  };

  // Money fields keep digits-only in state, display with commas
  const handleMoneyChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value.replace(/[^\d]/g, '') }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(
        isEdit ? `${API_URL}/api/jobs/${jobId}` : `${API_URL}/api/jobs`,
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...formData,
            budget: parseInt(formData.budget),
            minFollowers: formData.minFollowers ? parseInt(formData.minFollowers) : null,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Failed to ${isEdit ? 'update' : 'create'} job`);
      }

      navigate('/network/brand-dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="post-job-page">
      <div className="post-job-container">
        <div className="post-job-header">
          <Link to="/network/brand-dashboard" className="back-btn">
            ← Back to Dashboard
          </Link>
          <h1>{isEdit ? 'Edit Job' : 'Post a New Job'}</h1>
          <p>{isEdit ? 'Update your campaign details' : 'Create a campaign brief to connect with top creators'}</p>
        </div>

        <form onSubmit={handleSubmit} className="post-job-form">
          {error && <div className="error-message">{error}</div>}

          {/* Basic Info */}
          <section className="form-section">
            <h2>Campaign Details</h2>
            
            <div className="form-group">
              <label htmlFor="title">Campaign Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="e.g., Summer Product Launch Campaign"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                placeholder="Describe your campaign goals, brand message, and what you're looking for..."
                rows={5}
              />
            </div>

            <div className="form-group">
              <label htmlFor="niche">Target Niche</label>
              <select
                id="niche"
                name="niche"
                value={formData.niche}
                onChange={handleChange}
              >
                <option value="">Select a niche</option>
                <option value="food">Food & Culinary</option>
                <option value="lifestyle">Lifestyle</option>
                <option value="fitness">Fitness & Health</option>
                <option value="beauty">Beauty & Fashion</option>
                <option value="travel">Travel</option>
                <option value="tech">Technology</option>
                <option value="entertainment">Entertainment</option>
                <option value="other">Other</option>
              </select>
            </div>
          </section>

          {/* Content Requirements */}
          <section className="form-section">
            <h2>Content Requirements</h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="contentType">Content Type *</label>
                <select
                  id="contentType"
                  name="contentType"
                  value={formData.contentType}
                  onChange={handleChange}
                  required
                >
                  <option value="feed">Feed Post</option>
                  <option value="story">Story</option>
                  <option value="reel">Reel</option>
                  <option value="carousel">Carousel</option>
                  <option value="mixed">Mixed Content</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="postCount">Number of Posts *</label>
                <input
                  type="number"
                  id="postCount"
                  name="postCount"
                  value={formData.postCount}
                  onChange={handleChange}
                  required
                  min={1}
                  max={20}
                />
              </div>
            </div>

            {(formData.contentType === 'mixed' || formData.contentType === 'story') && (
              <div className="form-group">
                <label htmlFor="storyCount">Number of Stories</label>
                <input
                  type="number"
                  id="storyCount"
                  name="storyCount"
                  value={formData.storyCount}
                  onChange={handleChange}
                  min={0}
                  max={20}
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="requirements">Specific Requirements</label>
              <textarea
                id="requirements"
                name="requirements"
                value={formData.requirements}
                onChange={handleChange}
                placeholder="List any specific requirements: hashtags, mentions, content guidelines, usage rights, etc."
                rows={4}
              />
            </div>
          </section>

          {/* Budget & Timeline */}
          <section className="form-section">
            <h2>Budget & Timeline</h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="budget">Total Campaign Budget (USD) *</label>
                <div className="input-with-prefix">
                  <span className="input-prefix">$</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    id="budget"
                    name="budget"
                    value={formatMoney(formData.budget)}
                    onChange={handleMoneyChange}
                    required
                    placeholder="5,000"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="creatorCount">Number of Creators *</label>
                <input
                  type="number"
                  id="creatorCount"
                  name="creatorCount"
                  value={formData.creatorCount}
                  onChange={handleChange}
                  required
                  min={1}
                  max={50}
                  placeholder="3"
                />
                <span className="form-hint">How many creators do you intend to select for this campaign</span>
              </div>
            </div>

            {formData.budget && formData.creatorCount > 0 && (
              <div className="budget-breakdown">
                <span className="breakdown-label">Estimated budget per creator:</span>
                <span className="breakdown-value">
                  ~${Math.round(parseInt(formData.budget) / parseInt(formData.creatorCount)).toLocaleString()}
                </span>
                <span className="breakdown-hint">Transparent budgets help creators submit competitive, realistic bids</span>
              </div>
            )}

            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label htmlFor="deadline">Deadline</label>
              <input
                type="date"
                id="deadline"
                name="deadline"
                value={formData.deadline}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          </section>

          {/* Bidding Info */}
          <section className="form-section bidding-highlight-section">
            <h2>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{verticalAlign: 'middle', marginRight: '0.5rem'}}>
                <path d="M12 1v4M12 19v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M1 12h4M19 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
              </svg>
              Competitive Bidding
            </h2>
            <div className="bidding-benefits">
              <div className="benefit-card">
                <div className="benefit-icon">💰</div>
                <div className="benefit-content">
                  <h4>Cost Efficiency</h4>
                  <p>Creators compete to offer their best rates, maximizing your budget's impact</p>
                </div>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">🎯</div>
                <div className="benefit-content">
                  <h4>Best-Fit Selection</h4>
                  <p>Compare proposals side-by-side to find the perfect creator for your campaign</p>
                </div>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">⚡</div>
                <div className="benefit-content">
                  <h4>Exclusivity & Competition</h4>
                  <p>Limited creator spots drive urgency and attract top-tier talent eager to win your campaign</p>
                </div>
              </div>
            </div>
          </section>

          {/* Creator Requirements */}
          <section className="form-section">
            <h2>Creator Requirements</h2>

            <div className="form-group">
              <label htmlFor="minFollowers">Minimum Follower Count</label>
              <input
                type="number"
                id="minFollowers"
                name="minFollowers"
                value={formData.minFollowers}
                onChange={handleChange}
                placeholder="e.g., 10000"
                min={0}
              />
              <span className="form-hint">Leave blank to accept all creator sizes</span>
            </div>
          </section>

          {/* Submit */}
          <div className="form-actions">
            <Link to="/network/brand-dashboard" className="cancel-btn">
              Cancel
            </Link>
            <button type="submit" className="submit-btn" disabled={isLoading}>
              {isLoading ? (
                <span>{isEdit ? 'Saving...' : 'Creating...'}</span>
              ) : (
                <>
                  {isEdit ? 'Save Changes' : 'Post Job'}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PostJob;

